import { getSelectedVendorName } from "components/CashToSchoolShow";
import moment from "moment";
import { createSelector } from "reselect";
import {
  getAllStores,
  getSchoolYearByDate,
  Store,
  DeliveryCommodity,
  PurchaseCommodity,
  IncidentEvent,
  isDeliveryStore,
  isAttendanceStore,
  isPurchaseDetailStore,
  isIncidentStore,
  getStoreDate,
  isDeletedStore,
  isStockMovementStore,
  isMealAttendanceStore,
  isTakeHomeRationAttendanceStore,
  compareByDateAndModel,
  StockMovementStore,
  MeasureUnit,
  NewStockEvent,
  getLastStartedSchoolYear,
  isSyncedStore,
} from "data-handler/ducks/stores";

import { RootState } from "data-handler/rootReducer";
import { store } from "data-handler/store";
import {
  deliveryCategory,
  mixedCategory,
  purchaseDetailCategory,
  takeHomeRationCategory,
} from "SCConstants";

// ************************************************************

/**
 * Adds 2 quantity values at full precision. Rounding to each commodity's
 * measure-unit decimals happens once, at the end, in finalizeStockStatus.
 * Rounding per step would accumulate drift (e.g. an exact 0 reading as -0.01).
 */
const addQuantity = (valueA: string, valueB: string): string =>
  (Number(valueA) + Number(valueB)).toString();

const subQuantity = (valueA: string, valueB: string): string =>
  (Number(valueA) - Number(valueB)).toString();

export type CommodityStatus = {
  [commodityId: string]: string;
};

export type StockStatus = {
  measureUnits: { [commodityId: string]: MeasureUnit };
  delivery: CommodityStatus;
  purchasedetail: CommodityStatus;
  takehomeration: CommodityStatus;
};

const finalizeStockStatus = (status: StockStatus): StockStatus => {
  const round = (map: CommodityStatus): CommodityStatus =>
    Object.fromEntries(
      Object.entries(map).map(([id, value]) => {
        const decimals = status.measureUnits[id]?.decimals ?? 3;
        // `|| 0` strips the sign from -0 so tiny negatives don't render as "-0.00".
        const n = Number(Number(value).toFixed(decimals)) || 0;
        return [id, n.toFixed(decimals)];
      })
    );
  return {
    ...status,
    delivery: round(status.delivery),
    purchasedetail: round(status.purchasedetail),
    takehomeration: round(status.takehomeration),
  };
};

export const mergeStockMovements = (
  movementA: StockStatus,
  movementB: StockStatus
): StockStatus => {
  // merge measureUnits
  const measureUnits = { ...movementA.measureUnits, ...movementB.measureUnits };
  // merge delivery
  const delivery = { ...movementA.delivery };
  for (const commodityId of Object.keys(movementB.delivery)) {
    const quantityA = delivery[commodityId] || "0";
    const quantityB = movementB.delivery[commodityId];
    delivery[commodityId] = addQuantity(quantityA, quantityB);
  }
  // merge purchasedetail
  const purchasedetail = { ...movementA.purchasedetail };
  for (const commodityId of Object.keys(movementB.purchasedetail)) {
    const quantityA = purchasedetail[commodityId] || "0";
    const quantityB = movementB.purchasedetail[commodityId];
    purchasedetail[commodityId] = addQuantity(quantityA, quantityB);
  }
  // merge takehomeration
  const takehomeration = { ...movementA.takehomeration };
  for (const commodityId of Object.keys(movementB.takehomeration)) {
    const quantityA = takehomeration[commodityId] || "0";
    const quantityB = movementB.takehomeration[commodityId];
    takehomeration[commodityId] = addQuantity(quantityA, quantityB);
  }

  return {
    measureUnits,
    delivery,
    purchasedetail,
    takehomeration,
  };
};

/**
 * getStockMovement function returns stock movement by commodity id.
 * getDetailedStockMovement function returns stock movement by deliverycommodity_id and purchasedetail_commodity_id
 * What that means is it calculate stock status for each delivery seperately.
 */
export const getDetailedStockMovement = (
  store: StockMovementStore
): StockStatus => {
  const result: StockStatus = {
    measureUnits: {},
    delivery: {},
    purchasedetail: {},
    takehomeration: {},
  };

  if (isDeliveryStore(store)) {
    /*
     * - Delivery can have 2 kind of stock events inside.
     *   1) normal delivery stock event
     *   2) thr delivery stock event
     */
    for (const stockEvent of (store as any).stock_events || []) {
      const stockEventId: string | undefined = stockEvent.id?.toString();
      // If stock event is just being created,
      // It might not have an id.
      // In that case ignore stock event.
      if (stockEventId === undefined) {
        continue;
      }
      const stockEventQuantity = stockEvent.quantity;
      const stockEventMeasureUnit: MeasureUnit =
        typeof stockEvent.measure_unit === "object"
          ? stockEvent.measure_unit
          : {
              id: stockEvent.measure_unit || 1,
              name: "",
              symbol: "",
              decimals: 3,
            };
      const stockEventCategory: string =
        stockEvent.category || deliveryCategory;
      if (stockEventCategory === deliveryCategory) {
        // Update current delivery stock for this stock event
        const currentStock = result.delivery[stockEventId] || "0";
        result.delivery[stockEventId] = addQuantity(
          currentStock,
          stockEventQuantity
        );
        result.measureUnits[stockEventId] = stockEventMeasureUnit;
      } else if (stockEventCategory === takeHomeRationCategory) {
        // Update current thr stock for this stock event
        const currentStock = result.takehomeration[stockEventId] || "0";
        result.takehomeration[stockEventId] = addQuantity(
          currentStock,
          stockEventQuantity
        );
        result.measureUnits[stockEventId] = stockEventMeasureUnit;
      }
    }
  } else if (isPurchaseDetailStore(store)) {
    for (const stockEvent of (store as any).stock_events ||
      (store as any).commodities ||
      []) {
      const stockEventId: string | undefined = stockEvent.id?.toString();
      if (stockEventId === undefined) {
        continue;
      }
      const stockEventQuantity: string = stockEvent.quantity;
      const stockEventMeasureUnit: MeasureUnit =
        typeof stockEvent.measure_unit === "object"
          ? stockEvent.measure_unit
          : {
              id: stockEvent.measure_unit || 1,
              name: "",
              symbol: "",
              decimals: 3,
            };
      const currentStock = result.purchasedetail[stockEventId] || "0";

      result.purchasedetail[stockEventId] = addQuantity(
        currentStock,
        stockEventQuantity
      );

      result.measureUnits[stockEventId] = stockEventMeasureUnit;
    }
  } else if (isAttendanceStore(store)) {
    if (isMealAttendanceStore(store)) {
      /*
       * Meal attendance has consumption inside and consumption
       * can have 4 kinds of commodities
       * 1) normal delivery commodity consumption.
       * 2) purchase detail commodity consumption.
       * 3) take home ration commodity consumption.
       * 4) non-wfp commodity consumption. This version is not added to stock since they are used in place.
       */
      for (const stockEvent of store?.consumption?.stock_events || []) {
        const measureUnit: MeasureUnit =
          typeof stockEvent.measure_unit === "object"
            ? stockEvent.measure_unit
            : ({
                id: (stockEvent.measure_unit as number) || 1,
                name: "",
                symbol: "",
                decimals: 3,
              } as MeasureUnit);
        const stockEventCategory: string =
          stockEvent.category || stockEvent.meta?.category || deliveryCategory;

        const addCommodity = (
          commodityId: string,
          commodityQuantity: string
        ) => {
          if (stockEventCategory === deliveryCategory) {
            const currentStock = result.delivery[commodityId] || "0";
            result.delivery[commodityId] = subQuantity(
              currentStock,
              commodityQuantity
            );
            result.measureUnits[commodityId] = measureUnit;
          } else if (stockEventCategory === purchaseDetailCategory) {
            const currentStock = result.purchasedetail[commodityId] || "0";
            result.purchasedetail[commodityId] = subQuantity(
              currentStock,
              commodityQuantity
            );
            result.measureUnits[commodityId] = measureUnit;
          } else if (stockEventCategory === takeHomeRationCategory) {
            const currentStock = result.takehomeration[commodityId] || "0";
            result.takehomeration[commodityId] = subQuantity(
              currentStock,
              commodityQuantity
            );
            result.measureUnits[commodityId] = measureUnit;
          }
          // We ignore commodity if it is non-wfp since they do not get added to stock.
        };

        const sideEffects = stockEvent.side_effects || [];
        for (const sideEffect of sideEffects) {
          const targetId = sideEffect.related_event_id?.toString();
          const qty =
            sideEffect.qty_fulfilled !== undefined
              ? sideEffect.qty_fulfilled
              : stockEvent.quantity;
          if (targetId) {
            addCommodity(targetId, qty.toString());
          }
        }
      }
    } else if (isTakeHomeRationAttendanceStore(store)) {
      // Take home ration attendance store only has single kind of consumption type
      // we just add them as consumption to thr stock type.
      for (const stockEvent of store?.consumption?.stock_events || []) {
        const measureUnit: MeasureUnit =
          typeof stockEvent.measure_unit === "object"
            ? stockEvent.measure_unit
            : ({
                id: (stockEvent.measure_unit as number) || 1,
                name: "",
                symbol: "",
                decimals: 3,
              } as MeasureUnit);
        stockEvent.side_effects
          ?.filter((e) => e.related_event_type === "DEL")
          .forEach((sideEffect) => {
            const targetId = sideEffect.related_event_id?.toString();
            const qty =
              sideEffect.qty_fulfilled !== undefined
                ? sideEffect.qty_fulfilled
                : stockEvent.quantity;
            if (targetId) {
              const currentStock = result.takehomeration[targetId] || "0";
              result.takehomeration[targetId] = subQuantity(
                currentStock,
                qty.toString()
              );
              result.measureUnits[targetId] = measureUnit;
            }
          });
      }
    }
    // We ignore student attendance store since it does not have any consumption on it.
  } else if (isIncidentStore(store)) {
    /*
     * Incidents has 3 kind of entries.
     *   1) normal delivery incident.
     *   2) takehomeration delivery incident.
     *   3) purchase detail incident.
     */

    for (const stockEvent of (store as any).stock_events || []) {
      const stockEventQuantity: string = stockEvent.quantity;
      const stockEventMeasureUnit: MeasureUnit =
        typeof stockEvent.measure_unit === "object"
          ? stockEvent.measure_unit
          : {
              id: stockEvent.measure_unit || 1,
              name: "",
              symbol: "",
              decimals: 3,
            };
      const stockEventCategory: string =
        stockEvent.category || deliveryCategory;
      const deliveryTargetId = stockEvent.side_effects?.find(
        (e: any) => e.related_event_type === "DEL"
      )?.related_event_id;
      const purchaseTargetId = stockEvent.side_effects?.find(
        (e: any) => e.related_event_type === "PUR"
      )?.related_event_id;

      if (stockEventCategory === deliveryCategory && deliveryTargetId) {
        const commodityId: string = deliveryTargetId.toString();
        const currentStock = result.delivery[commodityId] || "0";
        result.delivery[commodityId] = subQuantity(
          currentStock,
          stockEventQuantity
        );
        result.measureUnits[commodityId] = stockEventMeasureUnit;
      } else if (
        stockEventCategory === purchaseDetailCategory &&
        purchaseTargetId
      ) {
        const commodityId: string = purchaseTargetId.toString();
        const currentStock = result.purchasedetail[commodityId] || "0";
        result.purchasedetail[commodityId] = subQuantity(
          currentStock,
          stockEventQuantity
        );
        result.measureUnits[commodityId] = stockEventMeasureUnit;
      } else if (
        stockEventCategory === takeHomeRationCategory &&
        deliveryTargetId
      ) {
        const commodityId: string = deliveryTargetId.toString();
        const currentStock = result.takehomeration[commodityId] || "0";
        result.takehomeration[commodityId] = subQuantity(
          currentStock,
          stockEventQuantity
        );
        result.measureUnits[commodityId] = stockEventMeasureUnit;
      }
    }
  }
  return result;
};

/**
 * Get stock movement for a single store.
 */
export const getStockMovement = (store: StockMovementStore): StockStatus => {
  const result: StockStatus = {
    measureUnits: {},
    delivery: {},
    purchasedetail: {},
    takehomeration: {},
  };

  if (isDeliveryStore(store)) {
    /*
     * - Delivery can have 2 kind of stock events inside.
     *   1) normal delivery stock event
     *   2) thr delivery stock event
     */
    for (const stockEvent of (store as any).stock_events || []) {
      const commodityId: string = (stockEvent.commodity || 0).toString();
      const stockEventQuantity = stockEvent.quantity;
      const stockEventMeasureUnit: MeasureUnit =
        typeof stockEvent.measure_unit === "object"
          ? stockEvent.measure_unit
          : {
              id: stockEvent.measure_unit || 1,
              name: "",
              symbol: "",
              decimals: 3,
            };
      const stockEventCategory: string =
        stockEvent.category || deliveryCategory;
      if (stockEventCategory === deliveryCategory) {
        // Update current delivery stock for this stock event
        const currentStock = result.delivery[commodityId] || "0";
        result.delivery[commodityId] = addQuantity(
          currentStock,
          stockEventQuantity
        );
        result.measureUnits[commodityId] = stockEventMeasureUnit;
      } else if (stockEventCategory === takeHomeRationCategory) {
        // Update current thr stock for this stock event
        const currentStock = result.takehomeration[commodityId] || "0";
        result.takehomeration[commodityId] = addQuantity(
          currentStock,
          stockEventQuantity
        );
        result.measureUnits[commodityId] = stockEventMeasureUnit;
      }
    }
  } else if (isPurchaseDetailStore(store)) {
    for (const stockEvent of (store as any).stock_events ||
      (store as any).commodities ||
      []) {
      const commodityId: string = (stockEvent.commodity || 0).toString();
      const stockEventQuantity: string = stockEvent.quantity;
      const stockEventMeasureUnit: MeasureUnit =
        typeof stockEvent.measure_unit === "object"
          ? stockEvent.measure_unit
          : {
              id: stockEvent.measure_unit || 1,
              name: "",
              symbol: "",
              decimals: 3,
            };
      const currentStock = result.purchasedetail[commodityId] || "0";

      result.purchasedetail[commodityId] = addQuantity(
        currentStock,
        stockEventQuantity
      );

      result.measureUnits[commodityId] = stockEventMeasureUnit;
    }
  } else if (isAttendanceStore(store)) {
    if (isMealAttendanceStore(store)) {
      /*
       * Meal attendance has consumption inside and consumption
       * can have 4 kinds of commodities
       * 1) normal delivery commodity consumption.
       * 2) purchase detail commodity consumption.
       * 3) take home ration commodity consumption.
       * 4) non-wfp commodity consumption. This version is not added to stock since they are used in place.
       */
      for (const stockEvent of store?.consumption?.stock_events || []) {
        const commodityId: string = (stockEvent.commodity || 0).toString();
        const stockEventQuantity = stockEvent.quantity;
        const stockEventMeasureUnit: MeasureUnit =
          typeof stockEvent.measure_unit === "object"
            ? stockEvent.measure_unit
            : ({
                id: (stockEvent.measure_unit as number) || 1,
                name: "",
                symbol: "",
                decimals: 3,
              } as MeasureUnit);
        const stockEventCategory: string =
          stockEvent.category || stockEvent.meta?.category || deliveryCategory;

        if (stockEventCategory === deliveryCategory) {
          const currentStock = result.delivery[commodityId] || "0";
          result.delivery[commodityId] = subQuantity(
            currentStock,
            stockEventQuantity
          );
          result.measureUnits[commodityId] = stockEventMeasureUnit;
        } else if (stockEventCategory === purchaseDetailCategory) {
          const currentStock = result.purchasedetail[commodityId] || "0";
          result.purchasedetail[commodityId] = subQuantity(
            currentStock,
            stockEventQuantity
          );
          result.measureUnits[commodityId] = stockEventMeasureUnit;
        } else if (stockEventCategory === takeHomeRationCategory) {
          const currentStock = result.takehomeration[commodityId] || "0";
          result.takehomeration[commodityId] = subQuantity(
            currentStock,
            stockEventQuantity
          );
          result.measureUnits[commodityId] = stockEventMeasureUnit;
        }
        // We ignore commodity if it is non-wfp since they do not get added to stock.
      }
    } else if (isTakeHomeRationAttendanceStore(store)) {
      // Take home ration attendance store only has single kind of consumption type
      // we just add them as consumption to thr stock type.
      for (const stockEvent of store?.consumption?.stock_events || []) {
        const commodityId: string = (stockEvent.commodity || 0).toString();
        const stockEventQuantity: string = stockEvent.quantity;
        const stockEventMeasureUnit: MeasureUnit =
          typeof stockEvent.measure_unit === "object"
            ? stockEvent.measure_unit
            : ({
                id: (stockEvent.measure_unit as number) || 1,
                name: "",
                symbol: "",
                decimals: 3,
              } as MeasureUnit);
        const currentStock = result.takehomeration[commodityId] || "0";

        result.takehomeration[commodityId] = subQuantity(
          currentStock,
          stockEventQuantity
        );
        result.measureUnits[commodityId] = stockEventMeasureUnit;
      }
    }
    // We ignore student attendance store since it does not have any consumption on it.
  } else if (isIncidentStore(store)) {
    /*
     * Incidents has 3 kind of entries.
     *   1) normal delivery incident.
     *   2) takehomeration delivery incident.
     *   3) purchase detail incident.
     */

    for (const stockEvent of (store as any).stock_events || []) {
      const commodityId: string = (stockEvent.commodity || 0).toString();
      const stockEventQuantity: string = stockEvent.quantity;
      const stockEventMeasureUnit: MeasureUnit =
        typeof stockEvent.measure_unit === "object"
          ? stockEvent.measure_unit
          : {
              id: stockEvent.measure_unit || 1,
              name: "",
              symbol: "",
              decimals: 3,
            };
      const stockEventCategory: string =
        stockEvent.category || deliveryCategory;

      if (stockEventCategory === deliveryCategory) {
        const currentStock = result.delivery[commodityId] || "0";
        result.delivery[commodityId] = subQuantity(
          currentStock,
          stockEventQuantity
        );
        result.measureUnits[commodityId] = stockEventMeasureUnit;
      } else if (stockEventCategory === purchaseDetailCategory) {
        const currentStock = result.purchasedetail[commodityId] || "0";
        result.purchasedetail[commodityId] = subQuantity(
          currentStock,
          stockEventQuantity
        );
        result.measureUnits[commodityId] = stockEventMeasureUnit;
      } else if (stockEventCategory === takeHomeRationCategory) {
        const currentStock = result.takehomeration[commodityId] || "0";
        result.takehomeration[commodityId] = subQuantity(
          currentStock,
          stockEventQuantity
        );
        result.measureUnits[commodityId] = stockEventMeasureUnit;
      }
    }
  }
  return result;
};

const getStockMovementStores = (
  date: string,
  granular: boolean = false,
  fullSearch: boolean = false
) => (state: RootState): StockMovementStore[] => {
  const schoolYear = getLastStartedSchoolYear(date)(state);
  const allStores = getAllStores(state);
  const endDate = date;
  // All stock stores between start of last school year and given date.
  const filteredStockStores = allStores
    .filter(isStockMovementStore)
    .filter(
      (s: StockMovementStore) =>
        (!isDeletedStore(s) &&
          moment(getStoreDate(s, granular)).isSameOrBefore(
            endDate,
            granular ? "seconds" : "days"
          )) ||
        (fullSearch &&
          !isDeletedStore(s) &&
          isSyncedStore(s) &&
          // do not take into account future synced purchases/deliveries
          !isDeliveryStore(s) &&
          !isPurchaseDetailStore(s) &&
          moment(getStoreDate(s, granular)).isSameOrBefore(
            schoolYear?.ends_on || endDate,
            granular ? "seconds" : "days"
          ))
    )
    .sort(compareByDateAndModel);

  return filteredStockStores;
};

/**
 * Calculates stock status of current date by tallying all stock movement stores.
 * Stock movement stores can be delivery, purchasedetail, attendance or incident.
 * Delivery and purchase detail are plus stock movements.
 * Consumption and incidents are minus stock movements.
 *
 * Here is all commodity types those stores might have:
 * - Delivery can have 2 kind of commodities inside.
 *   1) normal delivery commodity
 *   2) thr delivery commodity
 * - Purchase detail can have only one kind of commodity.(purchase detail)
 * - Consumptions are stored in attendance and then can have 4 kinds of commodities.
 *   1) normal delivery commodity consumption.
 *   2) purchase detail commodity consumption.
 *   3) take home ration commodity consumption.
 *   4) non-wfp commodity consumption. This version is not added to stock since they are used in place.
 * - Incidents has 3 kind of commodities.
 *   1) normal delivery incident commodity.
 *   2) takehomeration delivery incident commodity.
 *   3) purchase detail incident commodity.
 *   This function will return 3 types of stock statuses.
 *   - delivery stock status
 *   - purchase detail stock status
 *   - take home ration stock status
 */

// Cache for memoized stock status selectors
const stockStatusSelectorCache = new Map<
  string,
  (state: RootState) => StockStatus
>();

export const getStockStatus = (
  date: string,
  granular: boolean = false,
  fullSearch: boolean = false
): ((state: RootState) => StockStatus) => {
  const cacheKey = `${date}-${granular}-${fullSearch}`;

  if (!stockStatusSelectorCache.has(cacheKey)) {
    const selector = createSelector(
      [
        (state: RootState) =>
          getStockMovementStores(date, granular, fullSearch)(state),
      ],
      (stockMovementStores): StockStatus => {
        let result: StockStatus = {
          measureUnits: {},
          delivery: {},
          purchasedetail: {},
          takehomeration: {},
        };

        for (const store of stockMovementStores) {
          const stockMovement = getStockMovement(store);
          result = mergeStockMovements(result, stockMovement);
        }
        return finalizeStockStatus(result);
      }
    );
    stockStatusSelectorCache.set(cacheKey, selector);
  }

  return stockStatusSelectorCache.get(cacheKey)!;
};

/*
 * Calculates stock values for each delivery seperately.
 * Difference between getStockStatus and getDetailedStockStatus is
 * in getStockStatus, commodity_id is normal commodity id and
 * in getDetailedStockStatus commodity_id is delivery_commodity_id or purchase_detail_commodity_id
 */

// Cache for memoized detailed stock status selectors
const detailedStockStatusSelectorCache = new Map<
  string,
  (state: RootState) => StockStatus
>();

export const getDetailedStockStatus = (
  date: string
): ((state: RootState) => StockStatus) => {
  if (!detailedStockStatusSelectorCache.has(date)) {
    const selector = createSelector(
      [(state: RootState) => getStockMovementStores(date)(state)],
      (stockMovementStores): StockStatus => {
        let result: StockStatus = {
          measureUnits: {},
          delivery: {},
          purchasedetail: {},
          takehomeration: {},
        };

        for (const store of stockMovementStores) {
          const stockMovement = getDetailedStockMovement(store);
          result = mergeStockMovements(result, stockMovement);
        }
        return finalizeStockStatus(result);
      }
    );
    detailedStockStatusSelectorCache.set(date, selector);
  }

  return detailedStockStatusSelectorCache.get(date)!;
};

// ************************************************************

function sortByDateAndModel(a: Store, b: Store): number {
  // Function used to sort stores by date and model
  const dateA = (a as any).occurred_on || (a as any).occurred_at;
  const dateB = (b as any).occurred_on || (b as any).occurred_at;

  const isDeliveryOrPurchase = (s: Store) =>
    isDeliveryStore(s) || isPurchaseDetailStore(s);

  if (moment(dateA).isAfter(dateB, "days")) return 1;
  if (moment(dateA).isBefore(dateB, "days")) return -1;
  // if the date is the same, prioritize delivery or purchasedetail models
  if (isDeliveryOrPurchase(a) && !isDeliveryOrPurchase(b)) return -1;
  return 0;
}

const getDeliveryOrPurchaseDate = (store: Store): string | undefined => {
  if (isDeliveryStore(store)) return (store as any).occurred_at;
  if (isPurchaseDetailStore(store)) return (store as any).occurred_at;
  return undefined;
};

const getDeliveryOrPurchaseItems = (
  store: Store
): { commodity?: number; category?: string }[] => {
  if (isDeliveryStore(store)) return (store as any).stock_events || [];
  if (isPurchaseDetailStore(store)) return (store as any).stock_events || [];
  return [];
};

const getFilteredDeliveriesOrPuchasesWithCommodities = (
  stores: Store[],
  commodityId: number,
  category: string,
  startDate: moment.MomentInput,
  endDate: moment.MomentInput
) => {
  return stores.filter(
    (store) =>
      (isDeliveryStore(store) || isPurchaseDetailStore(store)) &&
      store.type !== "delete" &&
      moment(getDeliveryOrPurchaseDate(store)).isBetween(
        startDate,
        endDate,
        "days",
        "[]"
      ) &&
      getDeliveryOrPurchaseItems(store).find(
        (item) => item.commodity === commodityId && item.category === category
      )
  );
};

const sortDeliveryOrPurchaseStores = (stores: Store[], order = "ascending") => {
  return stores.sort((a, b) => {
    const tempStoreA = getDeliveryOrPurchaseDate(a);
    const tempStoreB = getDeliveryOrPurchaseDate(b);
    return order === "descending"
      ? moment(tempStoreA).isAfter(tempStoreB)
        ? -1
        : 1
      : moment(tempStoreA).isAfter(tempStoreB)
      ? 1
      : -1;
  });
};

const getFilteredStoresWithConsumptionAndIncidents = (
  deliveryCommodityId: number,
  category: string,
  startDate: moment.MomentInput,
  endDate: moment.MomentInput,
  stores: Store[]
) => {
  const sideEffectType: "DEL" | "PUR" =
    category === purchaseDetailCategory ? "PUR" : "DEL";
  return stores
    .filter((store) => {
      let stockEvents: NewStockEvent[] = [];
      let storeDate: string | undefined;
      if (isAttendanceStore(store)) {
        stockEvents = store.consumption?.stock_events || [];
        storeDate = store.occurred_on;
      } else if (isIncidentStore(store)) {
        stockEvents = store.stock_events || [];
        storeDate = store.occurred_at;
      } else {
        return false;
      }

      return (
        store.type !== "delete" &&
        moment(storeDate).isBetween(startDate, endDate, "days", "[]") &&
        stockEvents.find((stockEvent) => {
          if (isAttendanceStore(store)) {
            return true;
          }
          if (deliveryCommodityId === undefined) {
            return false;
          }
          const targetId = stockEvent.side_effects?.find(
            (e) => e.related_event_type === sideEffectType
          )?.related_event_id;
          return (
            targetId?.toString() === deliveryCommodityId?.toString() &&
            stockEvent.category === category
          );
        })
      );
    })
    .sort(sortByDateAndModel);
};

const getQuantityUsed = (
  stores: Store[],
  deliveryCommodityId: number,
  commodityId: number,
  category: string,
  allowConsumptionsNotMatched = false
) => {
  let quantityUsed = 0;

  const sideEffectType: "DEL" | "PUR" =
    category === purchaseDetailCategory ? "PUR" : "DEL";

  // Stores should only contain incidents and consumptions at this point
  stores.forEach((store) => {
    let stockEvents: NewStockEvent[] = [];
    if (isAttendanceStore(store)) {
      stockEvents = store.consumption?.stock_events || [];
    } else if (isIncidentStore(store)) {
      stockEvents = store.stock_events || [];
    } else {
      return;
    }

    stockEvents
      .filter(
        (stockEvent) =>
          stockEvent.commodity?.toString() === commodityId?.toString() &&
          stockEvent.category === category
      )
      .forEach((stockEvent) => {
        if (isAttendanceStore(store)) {
          if (allowConsumptionsNotMatched) {
            quantityUsed = parseFloat(
              (quantityUsed + parseFloat(stockEvent.quantity)).toFixed(10)
            );
          } else {
            stockEvent.side_effects
              ?.filter(
                (e) =>
                  e.related_event_type === sideEffectType &&
                  e.related_event_id?.toString() ===
                    deliveryCommodityId?.toString()
              )
              .forEach((sideEffect) => {
                const qty =
                  sideEffect.qty_fulfilled !== undefined
                    ? sideEffect.qty_fulfilled
                    : stockEvent.quantity;
                quantityUsed = parseFloat(
                  (quantityUsed + parseFloat(qty)).toFixed(10)
                );
              });
          }
        } else if (isIncidentStore(store)) {
          const targetId = stockEvent.side_effects?.find(
            (e) => e.related_event_type === sideEffectType
          )?.related_event_id;
          if (targetId?.toString() === deliveryCommodityId.toString()) {
            quantityUsed = parseFloat(
              (quantityUsed + parseFloat(stockEvent.quantity)).toFixed(10)
            );
          }
        }
      });
  });
  return quantityUsed;
};

/**
 * Returns the Deliveries (List) in Stock for the given commodityId
 */
export const deliveriesInStockByCommodityByCategory = (
  stores: Store[],
  commodityId: number,
  category: string,
  deliveryCommodityId: string | number | undefined = undefined,
  startDate: string | undefined = undefined,
  endDate = moment().format("YYYY-MM-DD"),
  includeOutOfStock = false
) => {
  const state = store.getState();
  const schoolYear = getSchoolYearByDate(startDate)(state);

  const deliveries: Store[] = [];
  // filter to stores which deal with commodities, particularly with commodityId
  const filteredDeliveries = getFilteredDeliveriesOrPuchasesWithCommodities(
    stores,
    commodityId,
    category,
    !startDate ? moment(0).format("YYYY-MM-DD") : schoolYear?.starts_on,
    endDate
  );

  // Get the deliveries, sorted
  const sortedArray = sortDeliveryOrPurchaseStores(filteredDeliveries);

  // Only keep the deliveries that have stock for the given commodityId
  sortedArray
    .filter((item) =>
      moment(getDeliveryOrPurchaseDate(item as Store)).isSameOrBefore(
        startDate || moment().format("YYYY-MM-DD"),
        "days"
      )
    )
    .forEach((store, index) => {
      (getDeliveryOrPurchaseItems(store as Store) as any[])
        .filter(
          (item) => item.commodity === commodityId && item.category === category
        )
        .forEach(({ quantity, id, batch_no, commodity, measure_unit }) => {
          const consumptionsAndIncidentStores = getFilteredStoresWithConsumptionAndIncidents(
            id,
            category,
            (store as any).occurred_at,
            endDate,
            stores.filter(
              (item) => isAttendanceStore(item) || isIncidentStore(item)
            )
          );

          const consumptionsUnsynced =
            (index >= 1 || (index === 0 && sortedArray.length > 1)) &&
            consumptionsAndIncidentStores.find(
              (item) => item.model === "attendance" && item.sync === false
            )
              ? true
              : false;

          let quantityUsed = getQuantityUsed(
            consumptionsAndIncidentStores,
            id as any,
            commodityId,
            category,
            index === 0 && sortedArray.length === 1
          );

          // Return deliveries with quantity available 0 if the delivery commodity id
          // matches the delivery commodity id param
          if (
            includeOutOfStock ||
            (deliveryCommodityId &&
              (deliveryCommodityId as unknown)?.toString() === id.toString() &&
              parseFloat(quantity) >= parseFloat(quantityUsed as any)) ||
            ((!deliveryCommodityId ||
              (deliveryCommodityId &&
                deliveryCommodityId.toString() !==
                  (id as unknown)?.toString())) &&
              parseFloat(quantity) > parseFloat(quantityUsed as any))
          ) {
            deliveries.push({
              waybill_no: (store as any).meta?.waybill_no,
              vendor_category: (store as any).meta?.vendor_category_id,
              occurred_at: (store as any).occurred_at,
              sync: (store as any).sync,
              objectCommodityId: id,
              commodityId: commodity,
              consumptionsUnsynced,
              quantityAvailable: (quantity as any) - quantityUsed,
              measure_unit: measure_unit,
              batch_no: batch_no,
            } as any);
          }
        });
    });
  return sortDeliveryOrPurchaseStores(deliveries, "descending");
};

/**
 * Takes in a ID from a commodity delivery, returns the delivery
 */
export const getDeliveryByCommodityAndDeliveryID = (
  stores: Store[],
  commodityDeliveryID: number
) => {
  return stores
    .filter(
      (store) =>
        isDeliveryStore(store) &&
        (store as any).stock_events &&
        store.type !== "delete"
    )
    .find((store) => {
      return (store as any).stock_events.find(
        (e: { id?: number }) => e.id === commodityDeliveryID
      );
    });
};
/**
 * Takes in a ID from a commodity delivery, returns the delivery
 */
export const getPurchaseByCommodityPurchaseID = (
  stores: Store[],
  commodityPurchaseID: number
) => {
  return stores
    .filter(
      (store) =>
        isPurchaseDetailStore(store) &&
        (store as any).stock_events &&
        store.type !== "delete"
    )
    .find((store) => {
      return (store as any).stock_events.find(
        (e: { id?: number }) => e.id === commodityPurchaseID
      );
    });
};

/**
 * Takes in a ID from a commodity delivery, returns the waybill of the delivery
 */
export const getWaybillByCommodityAndDeliveryID = (
  stores: Store[],
  commodityDeliveryID: number
) => {
  const delivery = getDeliveryByCommodityAndDeliveryID(
    stores,
    commodityDeliveryID
  ) as any;
  return delivery?.meta?.waybill_no;
};
/**
 * Takes in a ID from a commodity purchase and a list of vendorCategories
 * and returns the name of the corresponding purchase
 */
export const getPurchaseNameByCommodityPurchaseID = (
  stores: Store[],
  commodityPurchaseID: number,
  vendorCategories: string[],
  includeVendorCategory: boolean = true
) => {
  const purchase = getPurchaseByCommodityPurchaseID(
    stores,
    commodityPurchaseID
  );
  const vendorCategoryName = getSelectedVendorName(
    (purchase as any)?.meta?.vendor_category_id,
    vendorCategories
  );

  return (
    (includeVendorCategory ? `${vendorCategoryName}: ` : "") +
    `${moment((purchase as any)?.occurred_at).format("dddd, DD.MM.YYYY")}`
  );
};

/** Ids of every commodity ever referenced by the school's stock events */
export const getHistoricCommodityIds = (stores: Store[]): Set<string> => {
  const ids = new Set<string>();
  stores.forEach((store) => {
    const events = "stock_events" in store ? store.stock_events || [] : [];
    events.forEach((event) => {
      if (event.commodity !== undefined && event.commodity !== null) {
        ids.add(event.commodity.toString());
      }
    });
  });
  return ids;
};

export const getCommoditiesInStoreByCategory = (
  stores: Store[],
  commoditiesIndex: { [x: string]: any },
  isWfp: boolean,
  category: string,
  date?: string | undefined
) => {
  let startDate: string | undefined = moment(0).format("YYYY-MM-DD");
  let endDate: string | undefined = moment().format("YYYY-MM-DD");
  if (date) {
    const state = store.getState();
    const schoolYear = getSchoolYearByDate(date)(state);
    startDate = schoolYear?.starts_on;
    endDate = date;
  }
  const filteredStores = stores.filter(
    (store) =>
      (store.category === category || store.category === mixedCategory) &&
      (store as any).stock_events &&
      (startDate && endDate
        ? moment(
            (store as any).occurred_at || (store as any).occurred_on
          ).isBetween(startDate, endDate, "days", "[]")
        : true)
  );
  let commoditiesList: string[] = [];
  filteredStores.forEach((item) => {
    (((item as any).stock_events as unknown) as (
      | PurchaseCommodity
      | DeliveryCommodity
    )[]).forEach((commodity) => {
      if (
        !commoditiesList.includes(commodity.commodity?.toString()) &&
        category === commodity.category
      ) {
        commoditiesList.push(commodity.commodity?.toString());
      }
    });
  });

  const mappedCommoditiesList = commoditiesList.map(
    (id) => commoditiesIndex[id]
  );

  return isWfp !== undefined
    ? mappedCommoditiesList.filter((item) => item?.is_wfp === isWfp)
    : mappedCommoditiesList;
};

export const getPurchasedCommodities = (
  stores: Store[],
  commoditiesIndex: { [key: number]: PurchaseCommodity | DeliveryCommodity },
  date0: moment.MomentInput,
  date1: moment.MomentInput
) => {
  const filteredStores = stores.filter((store) => {
    return (
      (store as any).stock_events &&
      isPurchaseDetailStore(store) &&
      moment((store as any).occurred_at).isBetween(date0, date1, "days", "[]")
    );
  });
  let commoditiesList: number[] = [];

  filteredStores.forEach((item) => {
    (item as any).stock_events.forEach(
      (commodity: PurchaseCommodity | DeliveryCommodity) => {
        if (!commoditiesList.includes(commodity.commodity)) {
          commoditiesList.push(commodity.commodity);
        }
      }
    );
  });

  const commodities: (
    | PurchaseCommodity
    | DeliveryCommodity
  )[] = commoditiesList.map((id) => commoditiesIndex[id]);

  return commodities;
};

export const getOtherCostsInStore = (
  stores: Store[],
  purchaseOtherCosts: any,
  date0: moment.MomentInput,
  date1: moment.MomentInput
) => {
  const filteredStores = stores.filter(
    (store) =>
      isPurchaseDetailStore(store) &&
      (store as any).stock_events &&
      moment((store as any).occurred_at).isBetween(date0, date1, "days", "[]")
  );
  let otherCostList: any[] = [];

  filteredStores.forEach((item: any) => {
    item.other_costs?.forEach((other_cost: any) => {
      if (
        other_cost.other_cost &&
        !otherCostList.includes(other_cost.other_cost)
      ) {
        otherCostList.push(other_cost.other_cost);
      }
    });
  });

  const purchaseOtherCostsOptions = purchaseOtherCosts
    ? (Object as any).assign(
        ...Object?.values(purchaseOtherCosts?.results).map(
          ({ ...rest }: any) => ({
            [rest.id]: rest,
          })
        ),
        {}
      )
    : null;

  const otherCostsInStore =
    otherCostList
      .map((id) => purchaseOtherCostsOptions[id])
      .filter((i) => i !== undefined) || [];

  return otherCostsInStore;
};

/**
 * Returns a snapshot of the initial carry over stock on the given `date`
 *
 * in the shape of: {
 *   <commodity_id>: {
 *     "totalQuantity": x,
 *     "quantityPerBatchNo": {
 *       <batch_no>: {
 *         "quantity": y,
 *         "date": z,
 *       },
 *       ...
 *     }
 *   },
 *   ...
 * }
 */
export const getInitialCarryOverStock = (
  state: RootState,
  date: moment.MomentInput,
  onlyInitialStock: boolean = false
) => {
  const deliveries = getAllStores(state)
    .filter(
      (store) =>
        isDeliveryStore(store) &&
        moment((store as any).occurred_at).isSameOrBefore(date, "day") &&
        (!onlyInitialStock || (store as any).meta?.is_initial_stock === true)
    )
    .sort((a, b) => {
      const dateA = (a as any).occurred_at;
      const dateB = (b as any).occurred_at;
      return moment(dateA).isAfter(dateB) ? 1 : -1;
    });

  const accumulator: any = {};

  deliveries.forEach((stock) => {
    const deliveredAt = (stock as any).occurred_at;
    (stock as any).stock_events
      .filter((i: any) => {
        // Drop only events explicitly flagged as non-WFP that are not
        // carry-over; legacy and v3 sync payloads send no flags (undefined
        // or null) and their delivery-category events must be kept
        const isWfp = i.is_commodity_wfp ?? i.meta?.is_commodity_wfp;
        const isCarryOver = i.meta?.is_carry_over;
        return isWfp !== false || Boolean(isCarryOver);
      })
      .forEach((item: any) => {
        const rawQuantity = stock.id
          ? item.quantity_available ?? item.balance
          : item.quantity;
        const quantity = parseFloat(rawQuantity);
        let {
          commodity,
          category,
          batch_no,
          measure_unit,
          id: deliveryCommodityId,
        } = item;
        // Sync payloads carry the batch number in the event meta
        batch_no = batch_no ?? item.meta?.batch_no;
        if (category === "delivery" && quantity > 0) {
          if (!batch_no) {
            batch_no = "unknown";
          }
          if (!Object.keys(accumulator).includes(commodity.toString())) {
            accumulator[commodity] = {
              totalQuantity: quantity,
              measure_unit: measure_unit,
              quantityPerBatchNo: {
                [batch_no]: {
                  quantity: quantity,
                  date: deliveredAt,
                  deliveryCommodityIds: [deliveryCommodityId],
                  deliveryCommodityDetails: [
                    {
                      deliveryCommodityId: deliveryCommodityId,
                      quantity: quantity,
                      date: deliveredAt,
                    },
                  ],
                },
              },
            };
          } else {
            accumulator[commodity]["totalQuantity"] = parseFloat(
              (
                parseFloat(accumulator[commodity]["totalQuantity"] || 0) +
                parseFloat(quantity as any)
              ).toFixed(10)
            );

            if (
              Object.keys(
                accumulator[commodity]["quantityPerBatchNo"]
              ).includes(batch_no)
            ) {
              accumulator[commodity]["quantityPerBatchNo"][batch_no][
                "quantity"
              ] = parseFloat(
                (
                  parseFloat(
                    accumulator[commodity]["quantityPerBatchNo"][batch_no][
                      "quantity"
                    ] || 0
                  ) + parseFloat(quantity as any)
                ).toFixed(10)
              );
              // Add delivery commodity ID to the array
              if (
                !accumulator[commodity]["quantityPerBatchNo"][batch_no][
                  "deliveryCommodityIds"
                ]
              ) {
                accumulator[commodity]["quantityPerBatchNo"][batch_no][
                  "deliveryCommodityIds"
                ] = [];
              }
              accumulator[commodity]["quantityPerBatchNo"][batch_no][
                "deliveryCommodityIds"
              ].push(deliveryCommodityId);

              // Add detailed delivery commodity info
              if (
                !accumulator[commodity]["quantityPerBatchNo"][batch_no][
                  "deliveryCommodityDetails"
                ]
              ) {
                accumulator[commodity]["quantityPerBatchNo"][batch_no][
                  "deliveryCommodityDetails"
                ] = [];
              }
              accumulator[commodity]["quantityPerBatchNo"][batch_no][
                "deliveryCommodityDetails"
              ].push({
                deliveryCommodityId: deliveryCommodityId,
                quantity: quantity,
                date: deliveredAt,
              });
            } else {
              accumulator[commodity]["quantityPerBatchNo"][batch_no] = {
                quantity: quantity,
                date: deliveredAt,
                deliveryCommodityIds: [deliveryCommodityId],
                deliveryCommodityDetails: [
                  {
                    deliveryCommodityId: deliveryCommodityId,
                    quantity: quantity,
                    date: deliveredAt,
                  },
                ],
              };
            }
            accumulator[commodity]["quantityPerBatchNo"][batch_no][
              "date"
            ] = deliveredAt;
          }
        }
      });
  });

  return accumulator;
};

/**
 * Returns a snapshot of the initial carry over purchase stock on the given `date`
 *
 * in the shape of: {
 *   <commodity_id>: {
 *     "totalQuantity": x,
 *     "measure_unit": measureUnit,
 *     "purchases": [
 *       {
 *         "quantity": y,
 *         "date": z,
 *         "purchaseCommodityId": id
 *       },
 *       ...
 *     ]
 *   },
 *   ...
 * }
 */
export const getInitialCarryOverPurchaseStock = (
  state: RootState,
  date: moment.MomentInput,
  onlyInitialStock: boolean = false
) => {
  const purchases = getAllStores(state).filter(
    (store) =>
      isPurchaseDetailStore(store) &&
      moment((store as any).occurred_at).isSameOrBefore(date, "day") &&
      (!onlyInitialStock || (store as any).meta?.is_initial_stock === true)
  );

  const accumulator: any = {};

  purchases.forEach((stock) => {
    const purchasedAt = (stock as any).occurred_at;
    const stockEvents = (stock as any).stock_events || [];

    stockEvents.forEach((item: any) => {
      const rawQuantity =
        item.quantity_available ?? item.balance ?? item.quantity ?? 0;
      const quantity = parseFloat(rawQuantity);
      let {
        commodity,
        category,
        measure_unit,
        id: purchaseCommodityId,
        total_paid,
      } = item;
      if (category === "purchasedetail" && quantity > 0) {
        if (!Object.keys(accumulator).includes(commodity.toString())) {
          accumulator[commodity] = {
            totalQuantity: quantity,
            measure_unit: measure_unit,
            purchases: [
              {
                quantity: quantity,
                date: purchasedAt,
                purchaseCommodityId: purchaseCommodityId,
                total_paid: total_paid || "0",
              },
            ],
          };
        } else {
          accumulator[commodity]["totalQuantity"] = parseFloat(
            (
              parseFloat(accumulator[commodity]["totalQuantity"] || 0) +
              parseFloat(quantity as any)
            ).toFixed(10)
          );

          // Add this purchase to the purchases array
          accumulator[commodity]["purchases"].push({
            quantity: quantity,
            date: purchasedAt,
            purchaseCommodityId: purchaseCommodityId,
            total_paid: total_paid || "0",
          });
        }
      }
    });
  });

  return accumulator;
};

export const getInitialStockIncidents = (
  state: RootState,
  date: moment.MomentInput
) => {
  const incidents: IncidentEvent[] = getAllStores(state).filter(
    (store) =>
      isIncidentStore(store) &&
      store.type !== "delete" &&
      store.meta?.is_initial_stock_incident === true &&
      store.category !== "purchasedetail" &&
      moment(store.occurred_at).isSame(date, "day")
  ) as IncidentEvent[];
  const accumulator: any = {};

  incidents.forEach((incident) => {
    const occurredAt = incident.occurred_at;
    (incident.stock_events || []).forEach(
      ({ commodity, batch_no, quantity, measure_unit }: any) => {
        if (!Object.keys(accumulator).includes(commodity.toString())) {
          accumulator[commodity] = {
            totalQuantity: parseFloat(quantity),
            measure_unit: measure_unit,
            quantityPerBatchNo: {
              [batch_no]: {
                quantity: parseFloat(quantity),
                date: occurredAt,
              },
            },
          };
        } else {
          accumulator[commodity]["totalQuantity"] = parseFloat(
            (
              parseFloat(accumulator[commodity]["totalQuantity"] || 0) +
              parseFloat(quantity)
            ).toFixed(10)
          );

          if (
            Object.keys(accumulator[commodity]["quantityPerBatchNo"]).includes(
              batch_no
            )
          ) {
            accumulator[commodity]["quantityPerBatchNo"][batch_no][
              "quantity"
            ] = parseFloat(
              (
                parseFloat(
                  accumulator[commodity]["quantityPerBatchNo"][batch_no][
                    "quantity"
                  ] || 0
                ) + parseFloat(quantity)
              ).toFixed(10)
            );
          } else {
            accumulator[commodity]["quantityPerBatchNo"][batch_no] = {
              quantity: parseFloat(quantity),
              date: occurredAt,
            };
          }
          accumulator[commodity]["quantityPerBatchNo"][batch_no][
            "date"
          ] = occurredAt;
        }
      }
    );
  });

  return { quantityPerCommodity: accumulator, incidentObjects: incidents };
};

export const getInitialPurchaseStockIncidents = (
  state: RootState,
  date: moment.MomentInput
) => {
  const incidents: IncidentEvent[] = getAllStores(state).filter(
    (store) =>
      isIncidentStore(store) &&
      store.type !== "delete" &&
      store.meta?.is_initial_stock_incident === true &&
      store.category === "purchasedetail" &&
      moment(store.occurred_at).isSame(date, "day")
  ) as IncidentEvent[];
  const accumulator: any = {};

  incidents.forEach((incident) => {
    const occurredAt = incident.occurred_at;
    (incident.stock_events || []).forEach(
      ({ commodity, quantity, measure_unit }: any) => {
        if (!Object.keys(accumulator).includes(commodity.toString())) {
          accumulator[commodity] = {
            totalQuantity: parseFloat(quantity),
            measure_unit: measure_unit,
            purchases: [
              {
                quantity: parseFloat(quantity),
                date: occurredAt,
              },
            ],
          };
        } else {
          accumulator[commodity]["totalQuantity"] = parseFloat(
            (
              parseFloat(accumulator[commodity]["totalQuantity"] || 0) +
              parseFloat(quantity)
            ).toFixed(10)
          );

          accumulator[commodity]["purchases"].push({
            quantity: parseFloat(quantity),
            date: occurredAt,
          });
        }
      }
    );
  });

  return { quantityPerCommodity: accumulator, incidentObjects: incidents };
};

export const getInitialStockDeliveryByDate = (
  state: RootState,
  date: moment.MomentInput
) => {
  return getAllStores(state).find(
    (store) =>
      isDeliveryStore(store) &&
      moment((store as any).occurred_at).isSame(date, "day") &&
      (store as any).meta?.is_initial_stock === true
  );
};

export const getInitialStockPurchaseByDate = (
  state: RootState,
  date: moment.MomentInput
) => {
  return getAllStores(state).find(
    (store) =>
      isPurchaseDetailStore(store) &&
      moment((store as any).occurred_at).isSame(date, "day") &&
      (store as any).meta?.is_initial_stock === true
  );
};

/**
 * Returns the earliest date when a purchase commodity was used in consumption or incident
 * Returns undefined if the purchase commodity has never been used
 */
export const getEarliestUsageDateForPurchaseCommodity = (
  stores: Store[],
  purchaseCommodityId: number
): string | undefined => {
  let earliestDate: string | undefined = undefined;

  stores.forEach((store) => {
    if (store.type === "delete") return;

    if (store.model === "attendance" && store.consumption) {
      const stockEvents = store.consumption.stock_events || [];
      const matchingStockEvent = stockEvents.find((stockEvent) => {
        if (stockEvent.category !== purchaseDetailCategory) return false;
        return (stockEvent.side_effects || []).some(
          (e: any) =>
            e.related_event_type === "PUR" &&
            e.related_event_id?.toString() === purchaseCommodityId.toString()
        );
      });

      if (matchingStockEvent) {
        const occurredOn = store.occurred_on;
        if (!earliestDate || moment(occurredOn).isBefore(earliestDate, "day")) {
          earliestDate = occurredOn;
        }
      }
    }

    if (isIncidentStore(store)) {
      const stockEvents = store.stock_events || [];
      const matchingStockEvent = stockEvents.find((stockEvent) => {
        if (stockEvent.category !== purchaseDetailCategory) return false;
        const targetId = stockEvent.side_effects?.find(
          (e) => e.related_event_type === "PUR"
        )?.related_event_id;
        return targetId === purchaseCommodityId;
      });

      if (matchingStockEvent) {
        const occurredAt = store.occurred_at;
        if (!earliestDate || moment(occurredAt).isBefore(earliestDate, "day")) {
          earliestDate = occurredAt;
        }
      }
    }
  });

  return earliestDate;
};

/**
 * Returns the earliest date when a delivery commodity was used in consumption or incident
 * Returns undefined if the delivery commodity has never been used
 */
export const getEarliestUsageDateForDeliveryCommodity = (
  stores: Store[],
  deliveryCommodityId: number
): string | undefined => {
  let earliestDate: string | undefined = undefined;

  stores.forEach((store) => {
    if (store.type === "delete") return;

    if (store.model === "attendance" && store.consumption) {
      const stockEvents = store.consumption.stock_events || [];
      const matchingStockEvent = stockEvents.find((stockEvent) => {
        if (
          stockEvent.category !== deliveryCategory &&
          stockEvent.category !== takeHomeRationCategory
        )
          return false;
        return (stockEvent.side_effects || []).some(
          (e: any) =>
            e.related_event_type === "DEL" &&
            e.related_event_id?.toString() === deliveryCommodityId.toString()
        );
      });

      if (matchingStockEvent) {
        const occurredOn = store.occurred_on;
        if (!earliestDate || moment(occurredOn).isBefore(earliestDate, "day")) {
          earliestDate = occurredOn;
        }
      }
    }

    if (isIncidentStore(store)) {
      const stockEvents = store.stock_events || [];
      const matchingStockEvent = stockEvents.find((stockEvent) => {
        if (
          stockEvent.category !== deliveryCategory &&
          stockEvent.category !== takeHomeRationCategory
        ) {
          return false;
        }
        const targetId = stockEvent.side_effects?.find(
          (e) => e.related_event_type === "DEL"
        )?.related_event_id;
        return targetId === deliveryCommodityId;
      });

      if (matchingStockEvent) {
        const occurredAt = store.occurred_at;
        if (!earliestDate || moment(occurredAt).isBefore(earliestDate, "day")) {
          earliestDate = occurredAt;
        }
      }
    }
  });

  return earliestDate;
};
